import { EditOutlined } from '@ant-design/icons';
import { getVatCountryIsoCode } from '@mahawi/eshop-common/src/get-vat-country-iso-code';
import { getLowerCaseLanguageCode } from '@mahawi/eshop-common/src/translation';
import { type IPayment } from '@mahawi/eshop-common/src/types';
import {
  EAddressType,
  EReviewRejectionReason,
  EReviewStatusType,
  type ICarrierService,
  type ICountryCurrencyPrice,
  type ICurrencyType,
} from '@mahawi/eshop-common/src/types';
import { type ITag } from '@mahawi/eshop-common/src/types';
import { EOrderStatus } from '@mahawi/eshop-common/src/types';
import {
  Alert,
  Button,
  Col,
  DatePicker,
  Descriptions,
  Divider,
  Drawer,
  Grid,
  Input,
  Popconfirm,
  Row,
  Select,
  Skeleton,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import updateLocale from 'dayjs/plugin/updateLocale';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { AddressEditDrawer } from '../components/address-edit-drawer';
import AsyncFeedback from '../components/async-feedback';
import CopyToClipboard from '../components/copy-to-clipboard';
import DetailPageHeader from '../components/detail-page-header';
import ExternalLink from '../components/external-link';
import Link from '../components/link';
import OrderAttachments from '../components/order/attachments';
import { BasicOrderInfo } from '../components/order/basic-info';
import { BillingAddressSection } from '../components/order/billing-address-section';
import { DateInfo } from '../components/order/date-info';
import { DeliveryAddressSection } from '../components/order/delivery-address-section';
import OrderHistory from '../components/order/order-history';
import { PriceDisplay } from '../components/order/price-display';
import ProductTable from '../components/product/table';
import ReviewPreview from '../components/review/review-preview';
import StatusTag from '../components/status-tag';
import { tagRender } from '../components/tags/tag-render';
import OrderWarehouseOrders from '../components/warehouse-order/order-warehouse-orders';
import OrderWithdrawals from '../components/withdrawal/order-withdrawals';
import { EDateFormat } from '../fragments/date-format';
import { EMPTY_VALUE } from '../fragments/empty-value';
import {
  FakturoidInvoiceLink,
  FakturoidSubjectLink,
  getFakturoidInvoiceUrl,
} from '../fragments/fakturoid-url-component';
import { getPacketTrackingUrl } from '../fragments/packet-tracking-url';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { type RootState } from '../reducers';
import {
  carrierCreatePacket,
  carrierServicesLoad,
} from '../reducers/carrier/reducer';
import {
  orderCreateInvoice,
  orderFakturoidSyncContact,
  orderLoad,
  orderLoadCustomer,
  orderSetAttachmentPublic,
  orderSetOrderStatusCode,
  orderUpdateAddress,
  reviewSentToCustomer,
  reviewSetStatusCode,
  saveOrderTags,
  sentShippedToCustomer,
  sentShippingTimeToCustomer,
  warehousePlaceOrder,
} from '../reducers/order/reducer';
import { type IOrderInvoice } from '../reducers/order/types';
import {
  addManualPacket,
  packetLoadByOrder,
} from '../reducers/packets/reducer';
import { type IAdminCarrierPacket } from '../reducers/packets/types';
import { SPACING } from '../theme/tokens';

dayjs.extend(relativeTime);
dayjs.extend(updateLocale);

const currentLocale: string = navigator.language || 'en';
const language: string = currentLocale.split('-')[0];

dayjs.updateLocale(language, {
  weekStart: 1,
});

const { Text } = Typography;

const PRODUCT_LINK_PARAMS: Record<string, string> = { stock: 'open' };

function OrderContainer(): React.ReactElement {
  const dispatch = useAppDispatch();
  const orderOrder = useSelector((s: RootState) => s.Order.order);
  const orderLoadedAt = useSelector((s: RootState) => s.Order.loadedAt);
  const orderInProcess = useSelector((s: RootState) => s.Order.inProcess);
  const orderCustomer = useSelector((s: RootState) => s.Order.customer);
  const orderInvoices = useSelector((s: RootState) => s.Order.invoices);
  const orderFakturoidSyncInProcess = useSelector(
    (s: RootState) => s.Order.fakturoidSyncInProcess,
  );
  const orderFakturoidSyncResult = useSelector(
    (s: RootState) => s.Order.fakturoidSyncResult,
  );
  const ordersStatusTypeCodes = useSelector(
    (s: RootState) => s.Orders.statusTypeCodes,
  );
  const ordersReviewStatusTypeCodes = useSelector(
    (s: RootState) => s.Orders.reviewStatusTypeCodes,
  );
  const configState = useSelector((s: RootState) => s.Config);
  const languageState = useSelector((s: RootState) => s.Language);
  const tagsTags = useSelector((s: RootState) => s.Tags.tags);
  const currencyCurrencies = useSelector(
    (s: RootState) => s.Currency.currencies,
  );
  const countriesCountries = useSelector(
    (s: RootState) => s.Countries.countries,
  );
  const carrierCarrierServices = useSelector(
    (s: RootState) => s.Carrier.carrierServices,
  );
  const carrierInProcess = useSelector((s: RootState) => s.Carrier.inProcess);
  const packetsOrderPackets = useSelector(
    (s: RootState) => s.Packets.orderPackets,
  );
  const packetsInProcess = useSelector((s: RootState) => s.Packets.inProcess);

  const { orderUUID } = useParams();
  const { md: isDesktop } = Grid.useBreakpoint();

  const [openDrawer, setOpenDrawer] = useState(false);
  const [drawerTitle, setDrawerTitle] = useState<string>();
  const [drawerContent, setDrawerContent] = useState<React.ReactElement>();
  const [newOrderStatusCode, setNewOrderStatusCode] = useState<
    EOrderStatus | undefined
  >();
  const [newReviewStatusCode, setNewReviewStatusCode] = useState<
    EReviewStatusType | undefined
  >();
  const [newRejectionReason, setNewRejectionReason] = useState<
    EReviewRejectionReason | undefined
  >();
  const [orderTags, setOrderTags] = useState<string[]>([]);
  const [shippingDate, setShippingDate] = useState<dayjs.Dayjs>(dayjs());
  const [isTrackingDrawerOpen, setIsTrackingDrawerOpen] = useState(false);
  const [trackingVendorCode, setTrackingVendorCode] = useState<
    string | undefined
  >();
  const [trackingCarrierService, setTrackingCarrierService] = useState<
    ICarrierService | undefined
  >();
  const [trackingNumber, setTrackingNumber] = useState('');
  const [carrierService, setCarrierService] = useState<
    ICarrierService | undefined
  >();
  const [editingAddressType, setEditingAddressType] = useState<
    EAddressType | undefined
  >();

  const trackingVendorOptions = useMemo(
    () =>
      [
        ...new Set(
          carrierCarrierServices
            ?.filter((cs: ICarrierService): boolean => cs.isActive)
            .map((cs: ICarrierService) => cs.carrierVendor.code) ?? [],
        ),
      ]
        .sort()
        .map((code) => ({ label: code, value: code })),
    [carrierCarrierServices],
  );

  const trackingCarrierOptions = useMemo(
    () =>
      carrierCarrierServices
        ?.filter(
          (cs: ICarrierService): boolean =>
            cs.isActive && cs.carrierVendor.code === trackingVendorCode,
        )
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((cs: ICarrierService) => ({ label: cs.name, value: cs.name })) ??
      [],
    [carrierCarrierServices, trackingVendorCode],
  );

  const currencyType = useMemo((): ICurrencyType | undefined => {
    return currencyCurrencies.find(
      ({ isoCode }: ICurrencyType): boolean =>
        isoCode === orderOrder?.currencyPrice.isoCode,
    );
  }, [currencyCurrencies, orderOrder?.currencyPrice.isoCode]);

  const taxCountryTypeIsoCode = useMemo((): string => {
    if (!orderOrder) return 'CZE';

    if (orderOrder.taxCountryTypeIsoCode) {
      return orderOrder.taxCountryTypeIsoCode;
    }

    return getVatCountryIsoCode({
      billingAddress: orderOrder.billingAddress,
      deliveryAddress: orderOrder.deliveryAddress,
      deliveryAddressIsDifferent: orderOrder.deliveryAddressIsDifferent,
      excludedCountryIsoCode: 'CZE',
    });
  }, [orderOrder]);

  const orderPriceWithTax = useMemo((): number | undefined => {
    if (!orderOrder) return undefined;

    return orderOrder.currencyPrice.pricesWithTax.find(
      (price: ICountryCurrencyPrice): boolean =>
        price.isoCode === taxCountryTypeIsoCode,
    )?.price;
  }, [orderOrder, taxCountryTypeIsoCode]);

  useEffect((): void => {
    if (orderUUID) {
      dispatch(orderLoad(orderUUID));
      dispatch(packetLoadByOrder(orderUUID));
    }
    if (!carrierCarrierServices) {
      dispatch(carrierServicesLoad());
    }
  }, [dispatch, orderUUID]);

  const tagOptions = useMemo(
    () =>
      tagsTags?.map((tag: ITag) => ({
        label: tag.name,
        value: tag.color,
      })) ?? [],
    [tagsTags],
  );

  useEffect((): void => {
    if (orderOrder?.uuid) {
      dispatch(orderLoadCustomer(orderOrder.uuid));
    }
  }, [dispatch, orderOrder?.uuid]);

  useEffect((): void => {
    setOrderTags(orderOrder?.tags ?? []);
  }, [orderOrder?.tags]);

  const deliveryAddressCountryIsoCode = useMemo(
    (): string =>
      orderOrder?.deliveryAddressIsDifferent
        ? orderOrder.deliveryAddress.countryTypeIsoCode
        : (orderOrder?.billingAddress.countryTypeIsoCode ?? ''),
    [
      orderOrder?.deliveryAddressIsDifferent,
      orderOrder?.deliveryAddress.countryTypeIsoCode,
      orderOrder?.billingAddress.countryTypeIsoCode,
    ],
  );

  const homeDeliveryCarrierOptions = useMemo(
    () =>
      carrierCarrierServices
        ?.filter(
          (cs: ICarrierService): boolean =>
            cs.countryType.isoCode === deliveryAddressCountryIsoCode &&
            cs.isActive &&
            cs.isHomeDelivery,
        )
        .map((cs: ICarrierService) => ({ label: cs.name, value: cs.name })) ??
      [],
    [carrierCarrierServices, deliveryAddressCountryIsoCode],
  );

  const handleTogglePublic = useCallback(
    (attachmentUUID: string, isPublic: boolean): void => {
      if (orderOrder?.uuid) {
        dispatch(
          orderSetAttachmentPublic(orderOrder.uuid, attachmentUUID, isPublic),
        );
      }
    },
    [dispatch, orderOrder?.uuid],
  );

  const order = orderOrder;

  if (order === undefined) {
    return <Skeleton active />;
  }

  if (order === null) {
    return (
      <Alert
        title="Order not found"
        description={`Order with uuid ${orderUUID} not found.`}
        type="error"
        showIcon
      />
    );
  }

  const { deliveryAddress, discount } = order;

  return (
    <>
      <DetailPageHeader
        title="Order"
        tags={
          <>
            <StatusTag status={order.orderStatusCode} />
            <StatusTag status={order.lead.languageType.name} />
            <Tag color={order.isForCompany ? 'green' : 'blue'}>
              {order.isForCompany ? 'Is for company' : 'Is for person'}
            </Tag>
            {order.companyHasPricesWithoutTax && (
              <Tag color="orange">Company has prices without tax</Tag>
            )}
            {order.deliveryAddressIsDifferent && (
              <Tag color="purple">Delivery address is different</Tag>
            )}
          </>
        }
        uuid={order.uuid}
      />

      {/* ── Actions ────────────────────────────────────────────────────────── */}
      <Divider titlePlacement="left">Actions</Divider>

      <Space size={SPACING.xl} align="start" wrap>
        <Space orientation="vertical" size={SPACING.xs}>
          <Text type="secondary">Order status</Text>
          <Space size={SPACING.sm}>
            <Select
              style={{ width: '15rem' }}
              placeholder="Select new status"
              onChange={setNewOrderStatusCode}
              options={ordersStatusTypeCodes?.map((code: string) => ({
                value: code,
                label: code,
              }))}
            />
            <Popconfirm
              placement="bottom"
              title="Change order status?"
              onConfirm={(): void => {
                if (newOrderStatusCode) {
                  dispatch(
                    orderSetOrderStatusCode(order.uuid, newOrderStatusCode),
                  );
                }
              }}
              okText="Change"
              cancelText="Cancel"
            >
              <Button loading={orderInProcess} disabled={orderInProcess}>
                Change status
              </Button>
            </Popconfirm>
          </Space>
        </Space>

        <Space orientation="vertical" size={SPACING.xs}>
          <Text type="secondary">Warehouse basket</Text>
          <Popconfirm
            placement="bottom"
            title="Place order in warehouses?"
            description="Sends all order products to their respective warehouses."
            onConfirm={(): void => {
              dispatch(warehousePlaceOrder(order.uuid));
            }}
            okText="Place"
            cancelText="Cancel"
          >
            <Button
              loading={orderInProcess}
              disabled={
                orderInProcess || order.orderStatusCode !== EOrderStatus.PAID
              }
            >
              Place order in warehouses
            </Button>
          </Popconfirm>
        </Space>
      </Space>

      <AsyncFeedback inProcess={orderInProcess} />

      {/* ── Order details ──────────────────────────────────────────────────── */}
      <Divider titlePlacement="left">Order details</Divider>

      <Descriptions
        bordered
        column={{ xs: 1, sm: 2, md: 4 }}
        items={[
          {
            key: 'basic-info',
            label: 'Basic info',
            span: { xs: 1, sm: 2, md: 2 },
            children: (
              <BasicOrderInfo
                orderUuid={order.uuid}
                leadLanguageName={order.lead.languageType.name}
                config={configState}
                language={languageState}
              />
            ),
          },
          {
            key: 'price',
            label: 'Price',
            span: 1,
            children: (
              <PriceDisplay
                orderPriceWithTax={orderPriceWithTax}
                priceWithoutTax={order.currencyPrice.priceWithoutTax}
                deliveryFee={order.orderDelivery.fee}
                deliveryFeeWithoutTax={order.orderDelivery.feeWithoutTax}
                packagesCount={order.orderDelivery.packagesCount}
                companyHasPricesWithoutTax={order.companyHasPricesWithoutTax}
                currencyType={currencyType}
              />
            ),
          },
          {
            key: 'discount',
            label: 'Discount',
            span: 1,
            children: (
              <Space orientation="vertical" size={SPACING.xs}>
                <Space size={SPACING.xs}>
                  <Text type="secondary">Type:</Text>
                  <Text strong>
                    {discount.discountType.code || EMPTY_VALUE}
                  </Text>
                </Space>
                <Space size={SPACING.xs}>
                  <Text type="secondary">Code:</Text>
                  <Text strong>{discount.code || EMPTY_VALUE}</Text>
                </Space>
                <Space size={SPACING.xs}>
                  <Text type="secondary">Value:</Text>
                  <Text strong>{discount.value}</Text>
                </Space>
              </Space>
            ),
          },
          {
            key: 'dates',
            label: 'Dates',
            span: { xs: 1, sm: 1, md: 2 },
            children: <DateInfo order={order} />,
          },
          {
            key: 'tags',
            label: 'Tags',
            span: { xs: 1, sm: 1, md: 2 },
            children: (
              <Space size={SPACING.sm}>
                <Select
                  mode="multiple"
                  tagRender={tagRender}
                  value={orderTags}
                  style={{ width: '15rem' }}
                  options={tagOptions}
                  onChange={(value: string[]): void => setOrderTags(value)}
                />
                <Button
                  loading={orderInProcess}
                  disabled={orderInProcess}
                  onClick={(): void => {
                    dispatch(saveOrderTags(order.uuid, orderTags));
                  }}
                >
                  Save tags
                </Button>
              </Space>
            ),
          },
          {
            key: 'delivery-note',
            label: 'Delivery note',
            span: { xs: 1, sm: 2, md: 4 },
            children: <Text>{orderOrder?.deliveryNote ?? EMPTY_VALUE}</Text>,
          },
          {
            key: 'terms-version',
            label: 'Terms version',
            children: (
              <Text>{orderOrder?.termsVersionCode ?? EMPTY_VALUE}</Text>
            ),
          },
        ]}
      />

      {/* ── Customer ──────────────────────────────────────────────────────── */}
      <Divider titlePlacement="left">Customer</Divider>

      {orderCustomer === undefined ? (
        <Spin size="small" />
      ) : orderCustomer === null || orderCustomer.uuid === null ? (
        <Tag color="red">No customer assigned</Tag>
      ) : (
        <Space size={SPACING.xl} align="start">
          <Space orientation="vertical" size={SPACING.xs}>
            <Space size={SPACING.xs}>
              <Text type="secondary">UUID:</Text>
              <Text strong>{orderCustomer.uuid}</Text>
              <CopyToClipboard
                text={orderCustomer.uuid}
                size="small"
                type="link"
              />
              <Link
                to={`/crm/customer/${orderCustomer.uuid}`}
                label="View Customer"
                openInNewTab
              />
            </Space>
            <Space size={SPACING.xs}>
              <Text type="secondary">Fakturoid ID:</Text>
              {orderCustomer.fakturoidId !== null ? (
                <FakturoidSubjectLink id={orderCustomer.fakturoidId} />
              ) : (
                <Tag color="orange">not synced</Tag>
              )}
            </Space>
          </Space>

          <Space orientation="vertical" size={SPACING.xs}>
            <Space size={SPACING.sm}>
              <Popconfirm
                placement="bottom"
                title="Sync customer contact to Fakturoid?"
                description="Contact will be created or updated in Fakturoid."
                onConfirm={(): void => {
                  dispatch(orderFakturoidSyncContact(order.uuid));
                }}
                okText="Sync"
                cancelText="Cancel"
              >
                <Button
                  loading={orderFakturoidSyncInProcess}
                  disabled={orderFakturoidSyncInProcess}
                >
                  Sync to Fakturoid
                </Button>
              </Popconfirm>
              <Popconfirm
                placement="bottom"
                title="Generate invoice in Fakturoid?"
                description={
                  orderInvoices.length > 0
                    ? `This order already has invoice ${orderInvoices.map((inv: IOrderInvoice): string => inv.fakturoidNumber).join(', ')}.`
                    : undefined
                }
                onConfirm={(): void => {
                  dispatch(orderCreateInvoice(order.uuid));
                }}
                okText="Generate"
                cancelText="Cancel"
              >
                <Button>Generate invoice</Button>
              </Popconfirm>
            </Space>
            <Space size={SPACING.xs} wrap>
              <Text type="secondary">Invoices:</Text>
              {orderInvoices.length === 0 ? (
                <Text type="secondary">---</Text>
              ) : (
                orderInvoices.map((inv: IOrderInvoice): React.ReactElement => (
                  <Space key={inv.fakturoidId} size={SPACING.xs}>
                    <FakturoidInvoiceLink
                      number={inv.fakturoidNumber}
                      url={getFakturoidInvoiceUrl(inv.fakturoidId)}
                      invoiceType={inv.invoiceType}
                    />
                    <ExternalLink
                      to={inv.publicHtmlUrl}
                      label="public"
                      ariaLabel="Open public invoice"
                    />
                  </Space>
                ))
              )}
            </Space>
            {orderFakturoidSyncResult !== null && (
              <Tag color={orderFakturoidSyncResult.success ? 'green' : 'red'}>
                {orderFakturoidSyncResult.success
                  ? orderFakturoidSyncResult.operation
                  : orderFakturoidSyncResult.error}
              </Tag>
            )}
          </Space>
        </Space>
      )}

      {/* ── Products ──────────────────────────────────────────────────────── */}
      <Divider titlePlacement="left">Products</Divider>

      <ProductTable
        products={order.products}
        currencyType={currencyType}
        withCopyLinks
        taxCountryTypeIsoCode={taxCountryTypeIsoCode}
        productLinkParams={PRODUCT_LINK_PARAMS}
      />

      {/* ── Addresses ─────────────────────────────────────────────────────── */}
      <Divider titlePlacement="left">Addresses</Divider>

      <Descriptions
        bordered
        column={{ xs: 1, sm: 2 }}
        items={[
          {
            key: 'item',
            label: (
              <Space size={SPACING.xs}>
                <Text>Billing address</Text>
                <Tooltip title="Edit billing address">
                  <Button
                    size="small"
                    icon={<EditOutlined />}
                    aria-label="Edit billing address"
                    onClick={(): void =>
                      setEditingAddressType(EAddressType.BILLING)
                    }
                  />
                </Tooltip>
              </Space>
            ),
            span: 1,
            children: <BillingAddressSection order={order} />,
          },
          {
            key: 'item-2',
            label: (
              <Space size={SPACING.xs}>
                <Text>Delivery address</Text>
                {order.deliveryAddressIsDifferent && (
                  <Tooltip title="Edit delivery address">
                    <Button
                      size="small"
                      icon={<EditOutlined />}
                      aria-label="Edit delivery address"
                      onClick={(): void =>
                        setEditingAddressType(EAddressType.DELIVERY)
                      }
                    />
                  </Tooltip>
                )}
              </Space>
            ),
            span: 1,
            children: (
              <DeliveryAddressSection
                deliveryAddressIsDifferent={order.deliveryAddressIsDifferent}
                deliveryAddress={deliveryAddress}
              />
            ),
          },
        ]}
      />

      {editingAddressType && (
        <AddressEditDrawer
          open={!!editingAddressType}
          onClose={(): void => setEditingAddressType(undefined)}
          onSave={(address): void => {
            dispatch(
              orderUpdateAddress(order.uuid, editingAddressType, address),
            );
          }}
          title={
            editingAddressType === EAddressType.BILLING
              ? 'Edit billing address'
              : 'Edit delivery address'
          }
          address={
            editingAddressType === EAddressType.BILLING
              ? order.billingAddress
              : deliveryAddress
          }
          countries={countriesCountries}
          showCompanyFields={order.isForCompany}
          inProcess={orderInProcess}
        />
      )}

      {/* ── Shipments ─────────────────────────────────────────────────────── */}
      <Divider titlePlacement="left">Shipments</Divider>

      <Row gutter={[SPACING.xl, SPACING.lg]}>
        {/* Left: existing shipments + add tracking */}
        <Col xs={24} md={14}>
          <Space
            orientation="vertical"
            size={SPACING.md}
            style={{ width: '100%' }}
          >
            {packetsOrderPackets === undefined ? (
              <Spin size="small" spinning />
            ) : packetsOrderPackets.length > 0 ? (
              <Space
                orientation="vertical"
                size={SPACING.sm}
                style={{ width: '100%' }}
              >
                {packetsOrderPackets.map((packet: IAdminCarrierPacket) => {
                  const latestStatus =
                    packet.CarrierPacketStatuses.length > 0
                      ? packet.CarrierPacketStatuses[
                          packet.CarrierPacketStatuses.length - 1
                        ]
                      : null;
                  const trackingUrl = getPacketTrackingUrl(
                    packet.barcode,
                    packet.CarrierService.CarrierVendorType.code,
                  );
                  return (
                    <Space
                      key={packet.id}
                      orientation="vertical"
                      size={SPACING.xs}
                    >
                      <Space size={SPACING.xs}>
                        <Link
                          to={`/packet/${packet.id}`}
                          label={`Shipment — ${packet.thirdPartyId}`}
                          openInNewTab
                        />
                        {trackingUrl && (
                          <ExternalLink
                            to={trackingUrl}
                            label="Track"
                            ariaLabel="Open external tracking in new tab"
                          />
                        )}
                      </Space>
                      {latestStatus ? (
                        <>
                          <Space size={SPACING.xs}>
                            <Text type="secondary">Status:</Text>
                            <StatusTag status={latestStatus.status} />
                          </Space>
                          <Space size={SPACING.xs}>
                            <Text type="secondary">Carrier status:</Text>
                            <Text strong>{latestStatus.carrierStatus}</Text>
                          </Space>
                          {latestStatus.note && (
                            <Space size={SPACING.xs}>
                              <Text type="secondary">Note:</Text>
                              <Text>{latestStatus.note}</Text>
                            </Space>
                          )}
                        </>
                      ) : (
                        <Text type="secondary">No status yet</Text>
                      )}
                      {packet.billedShippingPriceConverted !== null &&
                        (() => {
                          const billed = packet.billedShippingPriceConverted!;
                          const fee = packet.Order.deliveryFeeWithoutTax;
                          const symbol = packet.Order.CurrencyType.symbol;
                          const diff = fee !== null ? billed - fee : null;
                          const sign = diff !== null && diff >= 0 ? '+' : '';

                          return (
                            <Space size={SPACING.xs}>
                              <Text type="secondary">Shipping billed:</Text>
                              <Text>
                                {packet.billedShippingPrice?.toFixed(2)}{' '}
                                {packet.billedShippingCurrency} →{' '}
                                {billed.toFixed(2)} {symbol}
                              </Text>
                              {diff !== null && (
                                <Text
                                  type={diff > 0 ? 'danger' : 'success'}
                                  strong
                                >
                                  ({sign}
                                  {diff.toFixed(2)} {symbol})
                                </Text>
                              )}
                            </Space>
                          );
                        })()}
                    </Space>
                  );
                })}
              </Space>
            ) : (
              <Text type="secondary">No shipments yet</Text>
            )}

            <Button onClick={(): void => setIsTrackingDrawerOpen(true)}>
              Add tracking number
            </Button>

            <Space orientation="vertical" size={SPACING.sm}>
              <Text type="secondary">Create delivery</Text>
              <Select
                style={{ width: '100%' }}
                placeholder="Select carrier service"
                onChange={(value: string): void => {
                  setCarrierService(
                    carrierCarrierServices?.find(
                      (cs: ICarrierService): boolean => cs.name === value,
                    ),
                  );
                }}
                options={homeDeliveryCarrierOptions}
              />
              <Button
                disabled={order.orderStatusCode !== EOrderStatus.PAID}
                onClick={(): void => {
                  if (carrierService) {
                    dispatch(
                      carrierCreatePacket(
                        order.uuid,
                        carrierService.carrierVendor.code,
                        carrierService.carrierService.thirdPartyId,
                      ),
                    );
                  }
                }}
              >
                Create delivery
              </Button>
            </Space>
          </Space>
        </Col>

        {/* Right: emails */}
        <Col xs={24} md={10}>
          <Space
            orientation="vertical"
            size={SPACING.lg}
            style={{ width: '100%' }}
          >
            <Space size={SPACING.xl} align="start" wrap>
              <Space orientation="vertical" size={SPACING.xs}>
                <Text type="secondary">Shipping date email</Text>
                <Space size={SPACING.sm} wrap>
                  <DatePicker
                    placeholder="Select date"
                    allowClear={false}
                    minDate={dayjs()}
                    defaultValue={shippingDate}
                    disabled={order.orderStatusCode !== EOrderStatus.PAID}
                    onChange={(date: dayjs.Dayjs | null): void => {
                      if (date) setShippingDate(date);
                    }}
                  />
                  <Popconfirm
                    placement="bottom"
                    title="Send shipping date to customer?"
                    onConfirm={(): void => {
                      dispatch(
                        sentShippingTimeToCustomer(
                          order.uuid,
                          shippingDate.format(EDateFormat.DATE),
                        ),
                      );
                    }}
                    okText="Send"
                    cancelText="Cancel"
                  >
                    <Button
                      disabled={
                        !shippingDate ||
                        shippingDate.isBefore(dayjs(), 'day') ||
                        order.orderStatusCode !== EOrderStatus.PAID
                      }
                    >
                      Send shipping date
                    </Button>
                  </Popconfirm>
                </Space>
              </Space>

              <Space orientation="vertical" size={SPACING.xs}>
                <Text type="secondary">Shipped email</Text>
                <Popconfirm
                  placement="bottom"
                  title="Send shipped email to customer?"
                  onConfirm={(): void => {
                    dispatch(sentShippedToCustomer(order.uuid));
                  }}
                  okText="Send"
                  cancelText="Cancel"
                >
                  <Button
                    disabled={order.orderStatusCode !== EOrderStatus.SHIPPED}
                  >
                    Send shipped email
                  </Button>
                </Popconfirm>
              </Space>
            </Space>
          </Space>
        </Col>
      </Row>

      <Drawer
        styles={{ wrapper: { width: isDesktop ? '40%' : '90%' } }}
        title="Add tracking number"
        placement="right"
        onClose={(): void => {
          setIsTrackingDrawerOpen(false);
          setTrackingVendorCode(undefined);
          setTrackingCarrierService(undefined);
          setTrackingNumber('');
        }}
        open={isTrackingDrawerOpen}
      >
        <Space
          orientation="vertical"
          style={{ width: '100%' }}
          size={SPACING.md}
        >
          <Select
            style={{ width: '100%' }}
            placeholder="Select vendor"
            loading={carrierInProcess}
            value={trackingVendorCode}
            onChange={(value: string): void => {
              setTrackingVendorCode(value);
              setTrackingCarrierService(undefined);
            }}
            options={trackingVendorOptions}
          />
          <Select
            showSearch={{ optionFilterProp: 'label' }}
            style={{ width: '100%' }}
            placeholder="Select carrier service"
            disabled={!trackingVendorCode}
            value={trackingCarrierService?.name}
            onChange={(value: string): void => {
              setTrackingCarrierService(
                carrierCarrierServices?.find(
                  (cs: ICarrierService): boolean => cs.name === value,
                ),
              );
            }}
            options={trackingCarrierOptions}
          />
          <Input
            placeholder="Tracking number"
            value={trackingNumber}
            onChange={(e): void =>
              setTrackingNumber(e.target.value.replace(/\s/g, ''))
            }
          />
          <Button
            type="primary"
            loading={packetsInProcess}
            disabled={
              !trackingCarrierService ||
              trackingNumber.trim().length < 3 ||
              packetsInProcess
            }
            onClick={(): void => {
              if (trackingCarrierService && trackingNumber.trim().length >= 3) {
                dispatch(
                  addManualPacket(
                    order.uuid,
                    trackingCarrierService.carrierVendor.code,
                    trackingCarrierService.carrierService.thirdPartyId,
                    trackingNumber.trim(),
                  ),
                );
                setIsTrackingDrawerOpen(false);
                setTrackingVendorCode(undefined);
                setTrackingCarrierService(undefined);
                setTrackingNumber('');
              }
            }}
          >
            Add
          </Button>
        </Space>
      </Drawer>

      <Divider titlePlacement="left">Attachments</Divider>

      <OrderAttachments
        orderUUID={order.uuid}
        onTogglePublic={handleTogglePublic}
        refreshKey={String(orderLoadedAt)}
      />

      <OrderWithdrawals orderUUID={order.uuid} />

      <OrderWarehouseOrders orderUUID={order.uuid} />

      {/* ── Review ────────────────────────────────────────────────────────── */}
      {order.review && (
        <>
          <Divider titlePlacement="left">
            {`Review — ${order.review.reviewStatusType.code}`}
          </Divider>

          <Space orientation="vertical" size={SPACING.sm}>
            <Space size={SPACING.sm}>
              <Select
                style={{ width: '15rem' }}
                onChange={setNewReviewStatusCode}
                options={ordersReviewStatusTypeCodes?.map((code: string) => ({
                  value: code,
                  label: code,
                }))}
              />
              {newReviewStatusCode === EReviewStatusType.REJECTED ? (
                <Select
                  style={{ width: '15rem' }}
                  placeholder="Rejection reason (required)"
                  value={newRejectionReason}
                  onChange={setNewRejectionReason}
                  status={newRejectionReason ? undefined : 'error'}
                  options={Object.values(EReviewRejectionReason).map(
                    (reason: EReviewRejectionReason) => ({
                      value: reason,
                      label: reason,
                    }),
                  )}
                />
              ) : null}
              <Popconfirm
                placement="bottom"
                title="Change review status?"
                onConfirm={(): void => {
                  if (newReviewStatusCode && order.review) {
                    dispatch(
                      reviewSetStatusCode(
                        order.review.uuid,
                        newReviewStatusCode,
                        newReviewStatusCode === EReviewStatusType.REJECTED
                          ? newRejectionReason
                          : undefined,
                      ),
                    );
                  }
                }}
                okText="Change"
                cancelText="Cancel"
              >
                <Button
                  disabled={
                    newReviewStatusCode === EReviewStatusType.REJECTED &&
                    !newRejectionReason
                  }
                >
                  Change review status
                </Button>
              </Popconfirm>
            </Space>

            <Space size={SPACING.sm}>
              <Text>{order.review.uuid}</Text>
              <CopyToClipboard text={order.review.uuid} type="link" />
              <ExternalLink
                to={`${configState.url}/${getLowerCaseLanguageCode(languageState.languageType)}/review/${order.review.uuid}`}
                ariaLabel="Open review in new tab"
              />
            </Space>

            {orderOrder?.review?.reviewStatusType.code &&
              [
                EReviewStatusType.IN_PROGRESS,
                EReviewStatusType.SUBMITTED,
                EReviewStatusType.APPROVED,
              ].includes(orderOrder.review.reviewStatusType.code) && (
                <Button
                  onClick={(): void => {
                    setDrawerTitle('Review');
                    setDrawerContent(<ReviewPreview />);
                    setOpenDrawer(!openDrawer);
                  }}
                >
                  Show review
                </Button>
              )}

            {orderOrder?.review?.reviewStatusType.code &&
              [EReviewStatusType.CREATED].includes(
                orderOrder.review.reviewStatusType.code,
              ) && (
                <Popconfirm
                  placement="bottom"
                  title="Send review email to customer?"
                  onConfirm={(): void => {
                    if (orderOrder?.uuid && orderOrder?.review?.uuid) {
                      dispatch(reviewSentToCustomer(orderOrder.uuid));
                    }
                  }}
                  okText="Send"
                  cancelText="Cancel"
                >
                  <Button>Send review email</Button>
                </Popconfirm>
              )}
          </Space>
        </>
      )}

      {/* ── Payments ──────────────────────────────────────────────────────── */}
      {order.payments.map((payment: IPayment): React.ReactElement => (
        <React.Fragment key={payment.uuid}>
          <Divider titlePlacement="left">
            {`Payment — ${payment.paymentVendorType} (${payment.paymentStatusType})`}
          </Divider>

          <Space orientation="vertical" size={SPACING.xs}>
            <Space size={SPACING.xs}>
              <Text type="secondary">UUID:</Text>
              <Text strong>{payment.uuid}</Text>
              <CopyToClipboard text={payment.uuid} type="link" />
            </Space>
            <Space size={SPACING.xs}>
              <Text type="secondary">ID:</Text>
              <Text strong>{payment.thirdPartyPaymentId}</Text>
              <CopyToClipboard text={payment.thirdPartyPaymentId} type="link" />
            </Space>
            <Button
              onClick={(): void => {
                setDrawerTitle('JSON response');
                setDrawerContent(
                  <Typography.Text>
                    <small>
                      <pre>
                        {JSON.stringify(payment.thirdPartyPaymentBody, null, 2)}
                      </pre>
                    </small>
                  </Typography.Text>,
                );
                setOpenDrawer(!openDrawer);
              }}
            >
              Show full JSON response
            </Button>
          </Space>
        </React.Fragment>
      ))}

      <Divider titlePlacement="left">History</Divider>

      <OrderHistory orderUUID={order.uuid} />

      <Drawer
        styles={{ wrapper: { width: isDesktop ? '75%' : '95%' } }}
        title={drawerTitle}
        placement="right"
        onClose={(): void => setOpenDrawer(false)}
        open={openDrawer}
      >
        {drawerContent}
      </Drawer>
    </>
  );
}

export default OrderContainer;
